import { Stethoscope, Heart, MapPin, Award } from "lucide-react";
import { BRAND, DISCLOSURES, IMAGES, SERVICE_AREA } from "@/config/site";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Section, SectionHeading } from "@/components/common/Section";
import { Reveal } from "@/components/common/Reveal";
import { FounderCard } from "@/components/common/FounderCard";
import { EmptyState } from "@/components/common/EmptyState";
import { CollectiveEndorsement } from "@/components/common/CollectiveEndorsement";
import { CTABand } from "@/components/common/CTABand";
import { LinkButton } from "@/components/common/LinkButton";

const VALUES = [
  { icon: Heart, title: "Client-Centered", text: "Care shaped around how you feel, move and recover." },
  { icon: MapPin, title: "Houston Roots", text: `Proudly serving individuals and organizations across ${SERVICE_AREA}.` },
  { icon: Award, title: "Legacy Forge Collective", text: `${BRAND.endorsement}, built for the long term.` },
];

export default function About() {
  useSeo({
    title: "About Legacy Spa | Black-Owned, Veteran-Owned Massage in Houston",
    description:
      "Legacy Spa is a Houston-based, Black-owned and veteran-owned massage therapy and wellness company. A Legacy Forge Collective Company. Where Wellness Meets Legacy.",
    path: "/about",
  });

  return (
    <>
      <PageHero
        eyebrow="About"
        titleLines={["Where Wellness", "Meets Legacy."]}
        lead="A Houston-based massage therapy and wellness company built on service, care and a lasting standard of quality."
        image={IMAGES.massageRoom}
        imageAlt="A serene, professional massage treatment space."
      />

      <Section className="bg-legacy-charcoal">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Our Story" title="Rooted in Houston." as="h2" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-white/70 leading-relaxed text-lg">
            <Reveal>
              <p>
                Legacy Spa is a massage therapy and wellness company serving
                Greater Houston through in-suite, mobile and corporate services.
                Our mission is simple: deliver professional, client-centered
                bodywork with a premium, welcoming standard.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-white/55 text-base">
                Legacy Spa is Black-owned and veteran-owned, and proudly {BRAND.endorsement}. As part of the Collective, the
                brand is built to grow responsibly and serve its community for
                years to come.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <CollectiveEndorsement className="pt-2" />
            </Reveal>
          </div>
        </div>
      </Section>

      <Section className="bg-[#12201d]">
        <div className="grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.05}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-legacy-mint/10 text-legacy-mint">
                  <v.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-2xl text-white">{v.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-legacy-charcoal">
        <SectionHeading eyebrow="Founder" title="Meet our founder." as="h2" />
        <div className="mt-14">
          <Reveal>
            <FounderCard />
          </Reveal>
        </div>
      </Section>

      <Section className="bg-[#12201d]">
        <SectionHeading eyebrow="Future Services" title="Medical massage." as="h2" />
        <div className="mt-10">
          <Reveal>
            <EmptyState
              icon={Stethoscope}
              title="Planned for a future phase"
              description={DISCLOSURES.medicalFuture}
            />
          </Reveal>
        </div>
      </Section>

      <CTABand
        eyebrow="Join us"
        title="Experience the Legacy standard."
        lead="Book an appointment or connect with our team."
        secondary={
          <LinkButton to="/contact" variant="outline" size="lg" testId="about-contact-btn">
            Contact Us
          </LinkButton>
        }
      />
    </>
  );
}
